import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const DataUpload: React.FC = () => {
  const [priceGuideFile, setPriceGuideFile] = useState<File | null>(null);
  const [productsFile, setProductsFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const { token, isAdmin } = useAuth();

  const handlePriceGuideChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setPriceGuideFile(event.target.files[0]);
    }
  };

  const handleProductsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setProductsFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!priceGuideFile || !productsFile) {
      setMessage('Please select both files');
      return;
    }

    if (!isAdmin()) {
      setMessage('Error: Admin privileges required to upload CardMarket data. Please contact an administrator.');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      if (!token) {
        throw new Error('Please login first');
      }

      // First create a snapshot
      const snapshotResponse = await fetch('http://localhost:8000/snapshots/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          snapshot_date: new Date().toISOString(),
          description: 'Uploaded CardMarket price and product data',
        }),
      });

      if (!snapshotResponse.ok) {
        const errorData = await snapshotResponse.json();
        throw new Error(`Failed to create snapshot: ${errorData.detail || 'Unknown error'}`);
      }

      const snapshot = await snapshotResponse.json();

      // Upload files to the snapshot
      const formData = new FormData();
      formData.append('price_guide_file', priceGuideFile);
      formData.append('products_file', productsFile);

      const uploadResponse = await fetch(
        `http://localhost:8000/snapshots/${snapshot.id}/ingest`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(`Failed to upload files: ${errorData.detail || 'Unknown error'}`);
      }

      const result = await uploadResponse.json();
      setMessage(`Success! Processed ${result.cards_processed} cards with ${result.prices_created} price entries.`);
      
      // Reset form
      setPriceGuideFile(null);
      setProductsFile(null);
      
    } catch (error) {
      setMessage(`Error: ${error instanceof Error ? error.message : 'Upload failed'}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Upload CardMarket Data</h1>
        
        {!isAdmin() && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Admin Access Required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Only administrators can upload CardMarket data. Please contact an administrator to upload price data.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Guide File (JSON)
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handlePriceGuideChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {priceGuideFile && (
                <p className="mt-1 text-sm text-green-600">Selected: {priceGuideFile.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Products File (JSON)
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleProductsChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {productsFile && (
                <p className="mt-1 text-sm text-green-600">Selected: {productsFile.name}</p>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || !priceGuideFile || !productsFile || !isAdmin()}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload and Process Data'}
            </button>

            {message && (
              <div className={`p-4 rounded-lg ${
                message.includes('Success') 
                  ? 'bg-green-50 text-green-800' 
                  : 'bg-red-50 text-red-800'
              }`}>
                {message}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Instructions</h3>
          <ul className="text-blue-700 space-y-1">
            <li>• Upload your CardMarket price guide JSON file</li>
            <li>• Upload your CardMarket products JSON file</li>
            <li>• The system will process and store the price data</li>
            <li>• Once uploaded, you can use this data to price decks</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataUpload;
